// GoogleMap.js
import React, { useCallback, useRef, useEffect, useState } from "react";
import { GoogleMap as GM, Marker, Circle, InfoWindow } from "@react-google-maps/api";
import { format_phone, format_phone_for_link } from "../../gloabal-functions/phone-number-correction";

const mapContainerStyle = {
  width: "100%",
  height: "100%"
};

function GoogleMap({ center, radius, providers, hoveredProviderId, setHoveredProviderId, options }) {
  const mapRef = useRef(null);
  const [infoWindowPosition, setInfoWindowPosition] = useState(null);
  const [hoveredProvider, setHoveredProvider] = useState(null);

  const prevCenterRef = useRef(center);
  const prevRadiusRef = useRef(radius);

  const onLoad = useCallback(map => {
    mapRef.current = map;
    // Fit bounds once on load if needed
    if (center && radius) {
      fitMapToRadius(center, radius);
      prevCenterRef.current = center;
      prevRadiusRef.current = radius;
    }
  }, []);

  useEffect(() => {
    // Only fit map if center or radius changed
    const centerChanged = prevCenterRef.current.lat !== center.lat || prevCenterRef.current.lng !== center.lng;
    const radiusChanged = prevRadiusRef.current !== radius;
    if (mapRef.current && (centerChanged || radiusChanged)) {
      fitMapToRadius(center, radius);
      prevCenterRef.current = center;
      prevRadiusRef.current = radius;
    }
  }, [center, radius]);

  useEffect(() => {
    if (hoveredProviderId) {
      const provider = providers.find(p => p.ID === hoveredProviderId);
      if (provider) {
        setHoveredProvider(provider);
        setInfoWindowPosition({
          lat: parseFloat(provider.lat),
          lng: parseFloat(provider.lng)
        });
      }
    } else {
      setHoveredProvider(null);
      setInfoWindowPosition(null);
    }
  }, [hoveredProviderId, providers]);

  const fitMapToRadius = (center, radius) => {
    const { lat, lng } = center;
    const radiusInMeters = radius * 1609.34;

    const latDelta = radiusInMeters / 111111;
    const lngDelta = radiusInMeters / (111111 * Math.cos((lat * Math.PI) / 180));

    const north = lat + latDelta;
    const south = lat - latDelta;
    const east = lng + lngDelta;
    const west = lng - lngDelta;

    const bounds = new window.google.maps.LatLngBounds({ lat: south, lng: west }, { lat: north, lng: east });
    mapRef.current.fitBounds(bounds, { padding: 50 });
    const currentZoom = mapRef.current.getZoom();
    // Increment zoom only if needed
    if (currentZoom) {
      mapRef.current.setZoom(currentZoom + 1);
    }
  };

  const handleMarkerMouseOver = provider => {
    setHoveredProviderId(provider.ID);
  };

  return (
    <GM mapContainerStyle={mapContainerStyle} center={center} onLoad={onLoad} options={options}>
      {providers &&
        providers.map(provider => {
          const position = {
            lat: parseFloat(provider.lat),
            lng: parseFloat(provider.lng)
          };
          return (
            <Marker
              key={provider.ID}
              position={position}
              title={provider.name}
              onMouseOver={() => handleMarkerMouseOver(provider)}
              icon={{
                url: "https://careavailability.com/wp-content/themes/care-availability/googlepin-paid.png",
                scaledSize: new window.google.maps.Size(20, 35)
              }}
            />
          );
        })}

      {hoveredProvider && infoWindowPosition && (
        <InfoWindow
          position={infoWindowPosition}
          onCloseClick={() => setHoveredProviderId(null)}
          options={{
            pixelOffset: new window.google.maps.Size(0, -30),
            disableAutoPan: true
          }}
        >
          <div className="flex">
            {hoveredProvider.logo && (
              <div className="w-16 p-1">
                <img src={hoveredProvider.logo} alt={hoveredProvider.name} className="w-full h-20" />
              </div>
            )}

            <div className="p-1 max-w-60">
              <h3 className="font-semibold text-lg">
                <a href={hoveredProvider.permalink}>{hoveredProvider.name}</a>
              </h3>
              <div>{hoveredProvider.street_address}</div>
              <div>
                {hoveredProvider.city}, {hoveredProvider.state} {hoveredProvider.zip}
              </div>
              <div className="font-semibold">
                <a href={format_phone_for_link(hoveredProvider.phone)}>{format_phone(hoveredProvider.phone)}</a>
              </div>
            </div>
          </div>
        </InfoWindow>
      )}
    </GM>
  );
}

export default React.memo(GoogleMap);
